import { EventContentArg } from '@fullcalendar/core'
import { FC, useMemo } from 'react'
import { CustomCalendar, ICustomCalendarData, ListLoading } from '../../../../components'
import {
  ESTADO_AGENDA_ICONES,
  ESTADO_AGENDA_LABELS,
  EstadoAgenda,
  formatFaixaHorario,
} from '../core/escalaHelpers'
import styles from '../core/styles/EscalaCalendario.module.css'

export type BlocoAgenda = {
  /** Id da agenda (um dia da escala), como string — é a chave do evento. */
  id: string
  /** `LocalDate` (`AAAA-MM-DD`). */
  data: string
  horarioInicio?: string | null
  horarioTermino?: string | null
  estado: EstadoAgenda
  /** Linha principal: profissional (eixo empresa) ou especialidades (eixo pessoal). */
  titulo?: string
  /** Linha secundária: empresas, tipos de atendimento etc. */
  detalhe?: string
  /**
   * `false` quando a tela não pode abrir, editar nem excluir o bloco. Hoje é o
   * caso da indisponibilidade global vista pela empresa — os endpoints de
   * detalhe devolvem 403 (ver `naoGerenciavelPelaEmpresa`).
   */
  gerenciavel: boolean
}

type Props = {
  blocos: BlocoAgenda[]
  isLoading?: boolean
  onVisibleRangeChange: (range: { inicio: string; termino: string }) => void
  /** Chamado ao acionar um bloco gerenciável. */
  onSelecionar?: (bloco: BlocoAgenda) => void
  /** Chamado ao acionar um bloco não gerenciável — trate com explicação, não com erro. */
  onSelecionarNaoGerenciavel?: (bloco: BlocoAgenda) => void
  /** Estados exibidos na legenda. */
  estadosNaLegenda?: EstadoAgenda[]
}

/**
 * Soma um dia a um `LocalDate` (`AAAA-MM-DD`) usando aritmética explícita em
 * UTC — o FullCalendar trata o `end` de evento de dia inteiro como exclusivo.
 * Não usa `new Date('AAAA-MM-DD')` no fuso local justamente para não repetir o
 * bug de "um dia a menos" documentado no CLAUDE.md do frontend.
 */
const proximoDia = (data: string): string => {
  const [ano, mes, dia] = data.split('-').map(Number)
  const referencia = new Date(Date.UTC(ano, mes - 1, dia + 1))
  return referencia.toISOString().slice(0, 10)
}

const classePorEstado: Record<EstadoAgenda, string> = {
  disponivel: styles.disponivel,
  indisponivel: styles.indisponivel,
  'indisponibilidade-global': styles.indisponibilidadeGlobal,
}

export const LegendaEstados: FC<{ estados: EstadoAgenda[] }> = ({ estados }) => (
  <ul
    className='d-flex flex-wrap align-items-center gap-3 list-unstyled mb-0 mt-4'
    aria-label='Legenda do calendário'
  >
    {estados.map((estado) => (
      <li key={estado} className={`${styles.itemLegenda} ${classePorEstado[estado]}`}>
        <i className={`bi ${ESTADO_AGENDA_ICONES[estado]}`} aria-hidden='true' />
        <span>{ESTADO_AGENDA_LABELS[estado]}</span>
      </li>
    ))}
  </ul>
)

export const EscalaCalendario: FC<Props> = ({
  blocos,
  isLoading = false,
  onVisibleRangeChange,
  onSelecionar,
  onSelecionarNaoGerenciavel,
  estadosNaLegenda = ['disponivel', 'indisponivel', 'indisponibilidade-global'],
}) => {
  const blocosPorId = useMemo(() => new Map(blocos.map((bloco) => [bloco.id, bloco])), [blocos])

  const eventos: ICustomCalendarData[] = useMemo(
    () =>
      blocos.map((bloco) => {
        const diaInteiro = !bloco.horarioInicio

        return {
          id: bloco.id,
          title: bloco.titulo ?? ESTADO_AGENDA_LABELS[bloco.estado],
          start: diaInteiro ? bloco.data : `${bloco.data}T${bloco.horarioInicio}`,
          end: diaInteiro
            ? proximoDia(bloco.data)
            : `${bloco.data}T${bloco.horarioTermino ?? bloco.horarioInicio}`,
          // O fundo do bloco vem das classes de estado, não do FullCalendar.
          color: 'transparent',
          display: 'block' as const,
          extendedProps: { blocoId: bloco.id },
        }
      }),
    [blocos]
  )

  const acionar = (bloco: BlocoAgenda) => {
    if (bloco.gerenciavel) onSelecionar?.(bloco)
    else onSelecionarNaoGerenciavel?.(bloco)
  }

  const renderEvento = (arg: EventContentArg) => {
    const bloco = blocosPorId.get(String(arg.event.extendedProps.blocoId ?? arg.event.id))
    if (!bloco) return null

    const rotulo = ESTADO_AGENDA_LABELS[bloco.estado]
    const faixa = formatFaixaHorario(bloco.horarioInicio, bloco.horarioTermino)
    const descricao = [rotulo, faixa, bloco.titulo, bloco.detalhe].filter(Boolean).join(' — ')
    const classes = `${styles.evento} ${classePorEstado[bloco.estado]}`

    const conteudo = (
      <>
        <span className='d-flex align-items-center gap-1 fw-semibold'>
          <i className={`bi ${ESTADO_AGENDA_ICONES[bloco.estado]}`} aria-hidden='true' />
          <span className='text-truncate'>{rotulo}</span>
        </span>
        <span className='d-block text-truncate'>{faixa}</span>
        {bloco.titulo && <span className='d-block text-truncate'>{bloco.titulo}</span>}
        {bloco.detalhe && <span className='d-block text-truncate'>{bloco.detalhe}</span>}
      </>
    )

    // O evento do FullCalendar não é focável por teclado; renderizamos um
    // <button> próprio para manter a navegação por teclado (WCAG 2.2 AA).
    return (
      <button
        type='button'
        className={classes}
        aria-label={
          bloco.gerenciavel
            ? `Abrir marcação: ${descricao}`
            : `${descricao}. Registrada pelo profissional para todas as instituições; não editável aqui.`
        }
        title={descricao}
        onClick={(event) => {
          event.stopPropagation()
          acionar(bloco)
        }}
      >
        {conteudo}
      </button>
    )
  }

  return (
    // `aria-busy` + `aria-live` anunciam a troca de mês/filtro para leitor de
    // tela — sem isso a recarga do calendário é silenciosa.
    <div className='position-relative' aria-live='polite' aria-busy={isLoading}>
      <CustomCalendar
        events={eventos}
        timeZone='local'
        allowedViews={['dayGridMonth', 'timeGridWeek', 'timeGridDay']}
        // Bloqueio de dia inteiro (indisponibilidade sem horário) só renderiza
        // fora da visão de mês com a faixa de all-day ligada.
        allDaySlot
        onVisibleRangeChange={onVisibleRangeChange}
        onClick={(info) => {
          const bloco = blocosPorId.get(String(info.event.extendedProps.blocoId ?? info.event.id))
          if (bloco) acionar(bloco)
        }}
        eventContent={renderEvento}
      />
      <LegendaEstados estados={estadosNaLegenda} />
      {isLoading && <ListLoading />}
    </div>
  )
}
